import React from 'react';
import type { RcTermView } from '../profile.types';

// Shared renderer for a term's exams → subject table + co-scholastic skills.
// Used by both Academics and Report Cards tabs.
export function TermBlock({ term }: { term: RcTermView }) {
  if (!term.exams.length) return null;
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>{term.name}</h2>
        {term.summary.percent != null && (
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
            Term average <b style={{ color: 'var(--text)' }}>{term.summary.percent}%</b>
          </span>
        )}
      </div>

      {term.exams.map((exam) => (
        <div key={exam.id} style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontWeight: 800, fontSize: 14 }}>{exam.name}</span>
            {exam.summary.percent != null && (
              <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-2)' }}>
                {exam.summary.marks_obtained}/{exam.summary.marks_max} · <b style={{ color: 'var(--text)' }}>{exam.summary.percent}%</b>
                {exam.summary.grade_label ? <span style={gradeChip}>{exam.summary.grade_label}</span> : null}
              </span>
            )}
          </div>

          {exam.subjects.length > 0 && (
            <table style={tbl}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Subject</th>
                  <th style={th}>Marks</th>
                  <th style={th}>%</th>
                  <th style={th}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {exam.subjects.map((s, i) => (
                  <tr key={i}>
                    <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{s.name}</td>
                    <td style={cell}>{s.is_absent ? 'AB' : (s.marks_obtained != null ? `${s.marks_obtained} / ${s.max_marks ?? '—'}` : '—')}</td>
                    <td style={cell}>{s.percent != null ? `${s.percent}%` : '—'}</td>
                    <td style={cell}>{s.grade_label ? <span style={gradeChip}>{s.grade_label}</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {exam.skills.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: exam.subjects.length ? '1px solid var(--divider)' : 'none' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>Co-Scholastic</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {exam.skills.map((s, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 999, fontSize: 13 }}>
                    {s.name} {s.is_absent ? <b>—</b> : <b style={gradeChip}>{s.grade_label || '—'}</b>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const tbl: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 };
const th: React.CSSProperties = { padding: '10px 16px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3, color: 'var(--text-2)', textAlign: 'center', borderBottom: '1px solid var(--divider)' };
const cell: React.CSSProperties = { padding: '11px 16px', textAlign: 'center', borderBottom: '1px solid var(--divider)', fontSize: 13.5 };
const gradeChip: React.CSSProperties = { display: 'inline-block', marginLeft: 6, padding: '1px 9px', borderRadius: 999, background: 'var(--slate-soft)', color: 'var(--text)', fontWeight: 700, fontSize: 12 };
